import React, { useEffect, useRef, useState } from 'react';
import { siftApiClient } from '../../api/siftApiClient';
import type {
  ClientParentSearchResult,
  ClientSearchResult,
  ClientVinSearchResult,
} from '../../api/types';
import './ClientBoardSearch.css';

interface ClientBoardSearchProps {
  /**
   * The seller filter values. A token is either a seller UID (one specific client),
   * a client NAME (every client called that), or a dealer-group PARENT NAME (every
   * client rolling up to it) — the backend matches all three columns.
   */
  selected: string[];
  onChange: (next: string[]) => void;
  /** Open a VIN's detail pop-out and scope the board to its client. */
  onPickVin: (vin: string, sellerUid?: string | null) => void;
}

const MIN_LEN = 2;
const DEBOUNCE_MS = 300;

/** UIDs are numeric; client names are not. Only used to label a restored token. */
const looksLikeUid = (token: string) => /^\d+$/.test(token);

/**
 * Open client + VIN search, replacing the seller dropdown. The dropdown was built
 * from the VIN parquet and so could only offer the ~28k clients holding a VIN; this
 * searches the client snapshot, where all 139k live — including the ones with no
 * inventory, who are exactly the clients a rep is hunting for.
 *
 * Selections live inside the panel rather than as chips under the box: the filter
 * row is fixed-height, and a growing chip list pushed the whole board down.
 */
export const ClientBoardSearch: React.FC<ClientBoardSearchProps> = ({ selected, onChange, onPickVin }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<ClientSearchResult[]>([]);
  const [vins, setVins] = useState<ClientVinSearchResult[]>([]);
  const [parents, setParents] = useState<ClientParentSearchResult[]>([]);
  // uid -> client name, so a specific pick reads as a name rather than a number.
  const [labels, setLabels] = useState<Record<string, string>>({});
  // Parent-group tokens, so the Selected list can say "Parent · N clients" rather
  // than showing a group name as if it were one dealer.
  const [parentTokens, setParentTokens] = useState<Record<string, number>>({});
  const abortRef = useRef<AbortController | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < MIN_LEN) {
      setClients([]); setVins([]); setParents([]); setLoading(false);
      abortRef.current?.abort();
      return;
    }
    const t = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      siftApiClient.searchClients(trimmed, 8, controller.signal)
        .then((res) => {
          if (controller.signal.aborted) return;
          setParents(res.parents);
          setClients(res.clients);
          setVins(res.vins);
          setLoading(false);
        })
        .catch((err) => {
          if (controller.signal.aborted || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
          setClients([]); setVins([]); setParents([]); setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const toggle = (token: string, label?: string) => {
    if (!token) return;
    if (label) setLabels((m) => ({ ...m, [token]: label }));
    onChange(selected.includes(token) ? selected.filter((s) => s !== token) : [...selected, token]);
    setQ('');
  };

  /** Same token list as a client pick — the backend also matches the parent-group
   * name — but remembered as a group so the Selected list can say so. */
  const toggleParent = (name: string, clients: number) => {
    if (!name) return;
    setParentTokens((m) => ({ ...m, [name]: clients }));
    onChange(selected.includes(name) ? selected.filter((s) => s !== name) : [...selected, name]);
    setQ('');
  };

  const describe = (token: string): { text: string; hint: string; kind: 'parent' | 'client' } => {
    const groupSize = parentTokens[token];
    if (groupSize != null) {
      return { text: token, hint: `${groupSize.toLocaleString()} clients in group`, kind: 'parent' };
    }
    if (labels[token]) return { text: labels[token], hint: `#${token}`, kind: 'client' };
    if (looksLikeUid(token)) return { text: `Client #${token}`, hint: '', kind: 'client' };
    return { text: token, hint: 'all clients with this name', kind: 'client' };
  };

  const searching = q.trim().length >= MIN_LEN;
  const placeholder = selected.length > 0
    ? `${selected.length} filter${selected.length === 1 ? '' : 's'} applied`
    : 'Search clients · groups · seller # · VIN';

  return (
    <div className="cbs-wrap" ref={wrapRef}>
      <div className={`cbs-field ${selected.length > 0 ? 'has-selection' : ''}`}>
        <input
          type="text"
          className="cbs-input"
          placeholder={placeholder}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          aria-label="Search clients by name, seller number, or VIN"
        />
        {(q || selected.length > 0) && (
          <button
            type="button"
            className="cbs-clear"
            onClick={() => { setQ(''); onChange([]); setOpen(false); }}
            aria-label="Clear client filter"
            title="Clear client filter"
          >
            ✕
          </button>
        )}
      </div>
      {open && (
        <div className="cbs-results" role="listbox">
          {selected.length > 0 && (
            <div className="cbs-group">
              <div className="cbs-group-head">
                <span>Selected · {selected.length}</span>
                <button type="button" className="cbs-clear-all" onClick={() => onChange([])}>Clear all</button>
              </div>
              {selected.map((token) => {
                const { text, hint, kind } = describe(token);
                return (
                  <button
                    key={`sel:${token}`}
                    type="button"
                    className="cbs-item"
                    onClick={() => toggle(token)}
                    title={`Remove ${text}`}
                  >
                    <span className="cbs-check">✓</span>
                    <span className="cbs-item-body">
                      <span className="cbs-item-name">
                        {kind === 'parent' && <span className="cbs-badge">Group</span>}
                        {text}
                      </span>
                      {hint && <span className="cbs-item-meta">{hint}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {searching && loading && <div className="cbs-state">Searching…</div>}

          {searching && !loading && clients.length === 0 && vins.length === 0 && parents.length === 0 && (
            <div className="cbs-state">Nothing matches “{q.trim()}”.</div>
          )}

          {/* Dealer groups first: picking one selects every client under it, which is
              a much wider action than picking a single dealer, so it must not look
              like just another client row. */}
          {searching && !loading && parents.length > 0 && (
            <div className="cbs-group">
              <div className="cbs-group-head"><span>Dealer groups (parent)</span></div>
              {parents.map((p) => (
                <button
                  key={`p:${p.client_parent_name}`}
                  type="button"
                  className="cbs-item"
                  onClick={() => toggleParent(p.client_parent_name, p.clients)}
                  title={`Filter to all ${p.clients} clients under ${p.client_parent_name}`}
                >
                  <span className="cbs-check">{selected.includes(p.client_parent_name) ? '✓' : ''}</span>
                  <span className="cbs-item-body">
                    <span className="cbs-item-name">
                      <span className="cbs-badge">Group</span>
                      {p.client_parent_name}
                    </span>
                    <span className="cbs-item-meta">
                      {p.clients.toLocaleString()} client{p.clients === 1 ? '' : 's'}
                      {p.listings ? ` · ${p.listings.toLocaleString()} listings` : ''}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {searching && !loading && clients.length > 0 && (
            <div className="cbs-group">
              <div className="cbs-group-head"><span>Clients (individual)</span></div>
              {clients.map((c) => {
                const name = c.seller_name || '';
                const shared = (c.name_count ?? 1) > 1;
                return (
                  <div key={c.seller_uid} className="cbs-row">
                    <button
                      type="button"
                      className="cbs-item"
                      onClick={() => toggle(c.seller_uid, name)}
                      title={name ? `Filter to ${name} (#${c.seller_uid})` : undefined}
                    >
                      <span className="cbs-check">{selected.includes(c.seller_uid) ? '✓' : ''}</span>
                      <span className="cbs-item-body">
                        <span className="cbs-item-name">{name || `Client ${c.seller_uid}`}</span>
                        <span className="cbs-item-meta">
                          #{c.seller_uid}
                          {c.client_parent_name ? ` · ${c.client_parent_name}` : ''}
                          {c.territory_market_center ? ` · ${c.territory_market_center}` : ''}
                          {c.listings ? ` · ${c.listings.toLocaleString()} listings` : ' · no listings'}
                        </span>
                      </span>
                    </button>
                    {shared && name && (
                      <button
                        type="button"
                        className="cbs-all-btn"
                        onClick={() => toggle(name)}
                        title={`Filter to all ${c.name_count} clients named ${name}`}
                      >
                        All {c.name_count}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {searching && !loading && vins.length > 0 && (
            <div className="cbs-group">
              <div className="cbs-group-head"><span>VINs</span></div>
              {vins.map((v) => (
                <button
                  key={v.vin}
                  type="button"
                  className="cbs-item"
                  onClick={() => { onPickVin(v.vin, v.seller_uid); setQ(''); setOpen(false); }}
                  title={`Open ${v.vin}`}
                >
                  <span className="cbs-check" />
                  <span className="cbs-item-body">
                    <span className="cbs-item-name">
                      {[v.model_year, v.make, v.model].filter(Boolean).join(' ') || v.vin}
                    </span>
                    <span className="cbs-item-meta">
                      {v.vin}{v.seller_name ? ` · ${v.seller_name}` : ''}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {!searching && selected.length === 0 && (
            <div className="cbs-state">Type at least {MIN_LEN} characters to search.</div>
          )}
        </div>
      )}
    </div>
  );
};
